import { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Receipt, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

export default function GstDetailsModal({ onClose }) {
  const { updateBillingDetails } = useAuth();
  const [gstNumber,      setGstNumber]      = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [saving,         setSaving]         = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!gstNumber.trim() && !billingAddress.trim())
      return toast.error("Enter at least a GST number or billing address");

    setSaving(true);
    try {
      const data = await updateBillingDetails({
        gstNumber:      gstNumber.trim()      || undefined,
        billingAddress: billingAddress.trim() || undefined,
      });
      toast.success(
        data.invoicesResent > 0
          ? `Saved — your invoice has been resent with the updated details`
          : "Billing details saved"
      );
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not save billing details");
    } finally {
      setSaving(false);
    }
  }

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{
          position: "fixed", inset: 0, zIndex: 950,
          background: "rgba(17,24,39,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}
      >
        <motion.div
          initial={{ scale: 0.96, y: 16, opacity: 0 }}
          animate={{ scale: 1,    y: 0,  opacity: 1 }}
          exit={{    scale: 0.96, y: 16, opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          style={{
            width: "100%", maxWidth: 440,
            background: "#ffffff", border: "1px solid #E5E7EB",
            borderRadius: 16, overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.14)",
          }}
        >
          <div style={{
            padding: "18px 22px", borderBottom: "1px solid #F3F4F6",
            display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: "#EEF2FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Receipt size={16} color="#4F46E5" />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Complete your billing details</div>
                <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 1 }}>
                  So we can send you a correct GST invoice
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: "none", border: "1px solid #E5E7EB", borderRadius: 8,
                width: 32, height: 32, cursor: "pointer", color: "#9CA3AF",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}
            ><X size={14} /></button>
          </div>

          <form onSubmit={handleSubmit} style={{ padding: "18px 22px 22px" }}>
            <div style={{ fontSize: 12.5, color: "#6B7280", lineHeight: 1.6, marginBottom: 16 }}>
              We didn't collect your GST number when you signed up. Add it below and
              we'll resend your invoice with the corrected details — to you and to our billing team.
            </div>

            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
              GST Number
            </label>
            <input
              value={gstNumber}
              onChange={e => setGstNumber(e.target.value.toUpperCase())}
              placeholder="e.g. 07AZZPG2440E1ZB"
              maxLength={15}
              style={{
                width: "100%", boxSizing: "border-box", height: 40, padding: "0 12px",
                background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 8,
                fontSize: 13.5, fontWeight: 600, color: "#111827", outline: "none",
                marginBottom: 14, letterSpacing: 0.5,
              }}
              onFocus={e => e.target.style.borderColor = "#4F46E5"}
              onBlur={e  => e.target.style.borderColor = "#E5E7EB"}
            />

            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
              Billing Address
            </label>
            <textarea
              value={billingAddress}
              onChange={e => setBillingAddress(e.target.value)}
              placeholder="Registered business address"
              maxLength={300}
              rows={3}
              style={{
                width: "100%", boxSizing: "border-box", padding: "10px 12px",
                background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 8,
                fontSize: 13, color: "#111827", outline: "none", resize: "vertical",
                marginBottom: 18, fontFamily: "inherit",
              }}
              onFocus={e => e.target.style.borderColor = "#4F46E5"}
              onBlur={e  => e.target.style.borderColor = "#E5E7EB"}
            />

            <button
              type="submit"
              disabled={saving}
              style={{
                width: "100%", height: 44, borderRadius: 10,
                background: saving ? "#9CA3AF" : "#4F46E5",
                border: "none", color: "#fff", fontSize: 14, fontWeight: 700,
                cursor: saving ? "default" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {saving
                ? <><Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> Saving…</>
                : "Save & resend invoice"}
            </button>

            <button
              type="button"
              onClick={onClose}
              style={{
                width: "100%", height: 36, marginTop: 8, background: "none", border: "none",
                color: "#9CA3AF", fontSize: 12.5, fontWeight: 600, cursor: "pointer",
              }}
            >
              Remind me later
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
